import React from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  return (
    <div className="pt-20">
      {/* Hero Section with Video */}
      <section className="relative h-[80vh] overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source
            src="https://customer-assets.emergentagent.com/job_aea33654-f6ae-462d-b335-69ffa6c4f9ae/artifacts/ko3q8s9c_Prewiev%20SM.MOV"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#20B2AA]/40 via-transparent to-[#FF69B4]/40"></div>
        <div className="relative h-full flex flex-col justify-between text-center px-4 py-12">
          <div className="flex-1 flex items-center justify-center">
            <div className="max-w-4xl">
              <h1 className="text-4xl md:text-6xl brand-script text-white mb-6 drop-shadow-2xl animate-fade-in-up flex items-center justify-center gap-2" data-testid="hero-title">
                Welcome to Strawberry Island! <span className="text-3xl md:text-4xl">🍓</span>
              </h1>
              <p className="text-xl md:text-2xl text-white drop-shadow-lg">
                Your ultimate adventure awaits in Tofino, BC
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pb-8">
            <Link
              to="/charters"
              className="bg-gradient-to-r from-[#20B2AA] to-[#5DADE2] text-white px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform duration-300 shadow-lg"
              data-testid="explore-charters-btn"
            >
              🎣 Explore Charters
            </Link>
            <Link
              to="/island-adventure"
              className="bg-gradient-to-r from-[#5DADE2] to-[#87CEEB] text-white px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform duration-300 shadow-lg"
              data-testid="book-tour-btn"
            >
              🗺️ Book a Tour
            </Link>
            <Link
              to="/cabins"
              className="bg-gradient-to-r from-[#FF69B4] to-[#FFB6C1] text-white px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform duration-300 shadow-lg"
              data-testid="book-cabin-btn"
            >
              🏠 Book a Cabin
            </Link>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#20B2AA' }}>
              The Ultimate Ocean Fishing Experience! 🌊
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              We go where the fish are! Our super-experienced guides are on a mission to give you 
              the most epic ocean fishing memories ever. Plus, we've got prawn and crab traps 
              scattered around the inlets – help us pull them and score yourself a fresh batch 
              of delicious seafood! 🐟🦀
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Fishing Charters */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="h-64 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#20B2AA]/80 to-[#5DADE2]/80"></div>
                <div className="absolute inset-0 grid grid-cols-2 gap-2 p-4">
                  <img
                    src="https://static.wixstatic.com/media/b9040e_0e7fc1db83fe43adabe0a89de9332d45~mv2.jpg"
                    alt="Screaming Eagle"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <img
                    src="https://static.wixstatic.com/media/b9040e_8b1553af0bcc4fceb26c346748ba8c31~mv2.jpg"
                    alt="Seahorse"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl brand-charters mb-3" style={{ color: '#FF69B4' }}>
                  Fishing Charters
                </h3>
                <p className="text-gray-600 mb-4">
                  Cast your line for Chinook & Coho Salmon, massive Halibut, Ling Cod, and more! 
                  Our guides know all the secret spots where the big ones bite.
                </p>
                <Link
                  to="/charters"
                  className="inline-block bg-gradient-to-r from-[#20B2AA] to-[#5DADE2] text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
                  data-testid="view-charters-btn"
                >
                  View Charters →
                </Link>
              </div>
            </div>

            {/* Island Adventure */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="h-64 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF69B4]/20 to-[#FFB6C1]/20"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/s6dm6wnn_2strawberryislandarial.png"
                  alt="Strawberry Island"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#20B2AA' }}>
                  Island Adventure
                </h3>
                <p className="text-gray-600 mb-4">
                  Explore pristine nature, spot incredible wildlife, soak in hot springs, 
                  and enjoy wellness retreats. Perfect for families and adventurers!
                </p>
                <Link
                  to="/island-adventure"
                  className="inline-block bg-gradient-to-r from-[#FF69B4] to-[#FFB6C1] text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
                  data-testid="view-adventure-btn"
                >
                  Explore Island →
                </Link>
              </div>
            </div>

            {/* Cabin Rentals */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="h-64 relative bg-gradient-to-br from-[#5DADE2]/80 to-[#87CEEB]/80 flex items-center justify-center p-4">
                <div className="grid grid-cols-2 grid-rows-2 gap-2 w-full h-full">
                  <img
                    src="https://static.wixstatic.com/media/b9040e_2a7a8b6cdd32427283a42bd2afaac1de~mv2.jpeg/v1/fill/w_300,h_225/b9040e_2a7a8b6cdd32427283a42bd2afaac1de~mv2.jpeg"
                    alt="Halibut House"
                    className="w-full h-full object-cover rounded-lg shadow-md"
                  />
                  <img
                    src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/cvy1u7qd__DSC0269.JPEG"
                    alt="Yellow House"
                    className="w-full h-full object-cover rounded-lg shadow-md"
                  />
                  <img
                    src="https://static.wixstatic.com/media/b9040e_58a848c30413494fb784b90f0a0000ca~mv2.jpeg/v1/fill/w_300,h_225/b9040e_58a848c30413494fb784b90f0a0000ca~mv2.jpeg"
                    alt="Seabass House"
                    className="w-full h-full object-cover rounded-lg shadow-md"
                  />
                  <div className="relative w-full h-full rounded-lg shadow-md overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/u1igskw8_IMG_7941.jpeg"
                      alt="La Perlita"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-2xl font-bold brand-script">Coming Soon</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#FF1493' }}>
                  Floating Cabins
                </h3>
                <p className="text-gray-600 mb-4">
                  Stay in unique floating cabins on Strawberry Island! Full kitchens, 
                  cozy linens, firewood, and even WiFi. Your own private island getaway!
                </p>
                <Link
                  to="/cabins"
                  className="inline-block bg-gradient-to-r from-[#5DADE2] to-[#87CEEB] text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
                  data-testid="view-cabins-btn"
                >
                  Book Cabin →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Showcase Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: '#FF69B4' }}>
            Reel Adventures, Reel Memories! 📸
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/zn1pojk5_IMG_3788.JPG"
                alt="Coastal Beauty"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/z69khqg0_IMG_3839.JPG"
                alt="Island Adventure"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/6nprh4iw_IMG_4200.JPG"
                alt="Fishing Memories"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/7fmvzkky_IMG_4489.JPG"
                alt="Tofino Waters"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
          <div className="text-center mt-8">
            <Link
              to="/gallery"
              className="inline-block bg-gradient-to-r from-[#FF69B4] to-[#FFB6C1] text-white px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform shadow-lg"
            >
              View Full Gallery →
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: '#FF69B4' }}>
            Why Strawberry Island Rocks! 🤘
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start space-x-4">
              <div className="text-4xl">✨</div>
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#20B2AA' }}>
                  15+ Years of Expertise
                </h3>
                <p className="text-gray-600">
                  Our experienced captains know these waters like the back of their hands. 
                  Safety, fun, and fish – guaranteed!
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="text-4xl">🦀</div>
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#20B2AA' }}>
                  Fresh Seafood Included
                </h3>
                <p className="text-gray-600">
                  Help pull our prawn and crab traps, and we'll send you home with a 
                  fresh batch cooked in garlic butter right on the boat!
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="text-4xl">🌲</div>
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#20B2AA' }}>
                  Hot Springs & Wildlife
                </h3>
                <p className="text-gray-600">
                  Visit the stunning Hot Springs Cove, spot bears, whales, and sea lions. 
                  Nature at its finest!
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="text-4xl">🏝️</div>
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#20B2AA' }}>
                  Private Island Paradise
                </h3>
                <p className="text-gray-600">
                  Stay in our unique floating cabins just 30 seconds from downtown Tofino. 
                  Your own slice of paradise!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#20B2AA] to-[#FF69B4] text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready for Your Strawberry Island Adventure? 🍓
          </h2>
          <p className="text-xl mb-8">
            Book your charter or cabin today and create memories that'll last a lifetime!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/charters"
              className="bg-white text-[#20B2AA] px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform shadow-lg"
              data-testid="cta-charters-btn"
            >
              Book a Charter 🎣
            </Link>
            <Link
              to="/cabins"
              className="bg-white text-[#FF69B4] px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform shadow-lg"
              data-testid="cta-cabins-btn"
            >
              Reserve a Cabin 🏠
            </Link>
            <a
              href="mailto:joel@fishingtofino.net?subject=I Am Interested In A Strawberry Island Activity"
              className="bg-white text-[#5DADE2] px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform shadow-lg"
              data-testid="cta-activities-btn"
            >
              Island Adventures 🏝️
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
